import * as React from 'react'

import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils' // Assumes a utility for merging class names

// A type for button variants that mirrors shadcn/ui's Button component props
type ActionVariant =
	| 'default'
	| 'destructive'
	| 'outline'
	| 'secondary'
	| 'ghost'
	| 'link'
	| 'success'

interface Props {
	title: string | JSX.Element
	show: boolean
	setShow: (show: boolean) => void
	onClose: () => void
	content?: JSX.Element
	actions?: {
		content?: JSX.Element
		text?: string
		variant?: ActionVariant
		onClick?: () => void
		disabled?: boolean
	}[]
	contentClassName?: string
}

const AstaDialog = React.forwardRef<HTMLDivElement, Props>(function AstaDialog(
	{ show, setShow, onClose, title, content, actions, contentClassName },
	ref
) {
	// Handles the open/close state changes triggered by the Dialog component
	// (e.g., pressing Escape, clicking the overlay)
	const handleOpenChange = React.useCallback(
		(open: boolean) => {
			if (!open) {
				setShow(false)
				onClose()
			}
		},
		[setShow, onClose]
	)

	return (
		<Dialog open={show} onOpenChange={handleOpenChange}>
			<DialogContent
				className="flex h-[80vh] min-w-[90vw] max-w-none flex-col p-0 text-foreground"
				ref={ref}
			>
				<DialogHeader className="flex-col items-start gap-4 border-b p-4 pr-12 sm:flex-row sm:items-center sm:p-6 sm:pr-14">
					<DialogTitle className="text-xl font-semibold w-full flex mr-4">
						{title}
					</DialogTitle>
				</DialogHeader>

				{/* Scrollable Content Area */}
				<div
					className={cn(
						'flex-1 overflow-y-auto overflow-x-auto p-6',
						contentClassName
					)}
				>
					{content}
				</div>

				{/* Footer with Actions */}
				<DialogFooter className="flex-col-reverse gap-2 border-t p-4 sm:flex-row sm:justify-end sm:p-6">
					<Button
						aria-label="Cancel"
						variant="outline"
						onClick={() => handleOpenChange(false)}
						className="w-full sm:w-auto"
					>
						Cancel
					</Button>
					{actions?.map((action, i) =>
						action.content ? (
							<div
								key={action.text ?? i}
								className="w-full sm:w-auto"
							>
								{action.content}
							</div>
						) : (
							<Button
								key={action.text ?? i}
								variant={action.variant ?? 'default'}
								onClick={action.onClick}
								disabled={action.disabled ?? false}
								className="w-full sm:w-auto"
							>
								{action.text}
							</Button>
						)
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
})

export default AstaDialog
